import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useBackdropClose } from 'hooks/useBackdropClose';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { MeetingRepository } from 'repository/meetingRepository';
import { isOkResponse, errorMessageOf } from 'common/utils/apiResponse';
import { ScheduleRepository } from 'repository/scheduleRepository';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import { isNativeApp, openNativePlaceExplore } from 'common/webview';
import { isInKorea } from 'common/utils/regionDetector';
import { toUtcISOString, localInputToUtc, parseTimeToMinutes } from 'common/utils/datetime';
import { useUserLocation } from 'hooks/useUserLocation';
import config from 'config/config';
import { useBackDismiss } from 'hooks/useBackDismiss';
import { PlaceMapView, MapPlace } from 'components/map/PlaceMapView';
import {
    IcClose, IcSparkle, IcMap,
    IcLandmark, IcRestaurant, IcCafe, IcHotel, IcPin, IcClock, IconProps,
} from 'components/common/icons/EmojiIcons';
import styles from './SubPlaceModal.module.css';
import { fetchPlacePhotos, pickPhotoUrl } from 'common/utils/placePhotos';
import { haversineMeters } from 'common/utils/geo';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface SubPlace {
    subPlaceId: number;
    meetingId?: string;
    placeId?: string;          // 장소 ID (place 테이블 참조)
    placeName: string;
    placeAddress?: string;
    latitude?: number;
    longitude?: number;
    category: string;
    sortOrder: number;
    dayNumber?: number;
    visitTime?: string;
    startDatetime?: string;
    endDatetime?: string;
    description?: string;
    imageUrl?: string;
    rating?: number;
    priceRange?: string;
    tags?: string;
    createdAt?: string;
    /** 담은 사람 닉네임 — 장소마다 누가 정했는지 보여 줍니다 */
    createdByNickname?: string;
}

export interface SubPlaceForm {
    placeName: string;
    placeAddress: string;
    category: string;
    dayNumber: number;
    dayNumbers?: number[]; // 복수 일자 선택 (여행 모임, HOTEL 제외)
    visitTime: string;
    description: string;
    // HOTEL 전용 (YYYY-MM-DD / HH:mm)
    checkInDate?: string;
    checkInTime?: string;
    checkOutDate?: string;
    checkOutTime?: string;
    // DAY 모임 비-HOTEL 전용 (datetime-local 'YYYY-MM-DDTHH:mm')
    // 모임 본 장소 시간/기존 sub_place 와 시간 겹침 검증 + 종료가 모임 종료보다 늦으면 모임 자동 연장.
    startDatetimeLocal?: string;
    endDatetimeLocal?: string;
    // 장소 좌표 (PlaceSearch / PlaceExplore / 지도 검색에서 선택 시 채워짐)
    // 이 값이 누락되면 미니지도가 좌표 없이 저장돼 "다음 이동 장소" 표시가 깨짐
    latitude?: number;
    longitude?: number;
    placeId?: string;
}

interface SubPlaceModalProps {
    isOpen: boolean;
    onClose: () => void;
    meetingId: string | undefined;
    editingSubPlace: SubPlace | null;
    isTravelMeeting: boolean;
    travelPeriod: { days: number; nights: number; start: Date; end: Date } | null;
    meetingTime?: string;
    subPlaces: SubPlace[];
    onSaveSuccess: () => void;
    returnTo: string;
    initialForm?: SubPlaceForm;
    initialIsCustomVisitTime?: boolean;
    /** 여행 모임의 여행지 이름 — 호텔 카테고리 지도 검색 시 좌표 anchor 로 활용 */
    travelDestinationName?: string;
    /** 여행지 좌표 (parent 가 findKnownDestinationCenter 등으로 미리 계산해 전달) */
    travelDestinationCenter?: { lat: number; lng: number } | null;
    /**
     * DAY 모임 비-HOTEL 시작/종료 시간 검증 + 자동 연장에 사용.
     * 셋 다 있어야 동작:
     *  - meetingScheduleStartDatetime / EndDatetime: 본 장소 시간 (겹침 체크 anchor + 디폴트)
     *  - meetingScheduleId: 자동 연장 시 PUT /schedules/{id} 호출용
     * 미전달 시 기존 visitTime 입력 UI 가 그대로 노출됨 (하위 호환).
     */
    meetingScheduleStartDatetime?: string | null;
    meetingScheduleEndDatetime?: string | null;
    meetingScheduleId?: string | null;
    /** 본 장소 이름 — 시간 겹침 알림 메시지에 사용 */
    mainPlaceName?: string;
    /**
     * 본 장소 (meeting.place_id) 편집 모드.
     * 이 객체가 있으면 sub_place 가 아니라 모임 본 장소 + schedule 을 편집한다.
     * 저장은 onSaveMainPlace 콜백으로 위임 (parent 가 PUT /place + PUT /schedules 호출).
     * editingSubPlace 와 동시에 set 하지 말 것.
     */
    editingMainPlace?: {
        placeName: string;
        placeAddress?: string;
        latitude?: number;
        longitude?: number;
    } | null;
    onSaveMainPlace?: (form: {
        placeName: string;
        placeAddress?: string;
        latitude?: number;
        longitude?: number;
        startDatetimeLocal: string;
        endDatetimeLocal: string;
        scheduleChanged: boolean;
    }) => Promise<void> | void;
    /**
     * 숙박 전용 모드 — HOTEL 카테고리 강제 + 카테고리 chip 그룹 숨김.
     * v6 상세 [+ 숙박 등록] 흐름에서 사용. 관광지·식당·카페 chip 을 안 보여서 UX 혼동 방지.
     */
    lockCategoryToHotel?: boolean;
    /**
     * 관광지 전용 모드 — HOTEL 을 카테고리 chip 목록에서 제거.
     * v6 [+ 관광지 추가] 흐름에서 숙박이 섞이지 않도록.
     */
    hideHotelInCategoryPicker?: boolean;
}

// Default visit time options — 라벨은 현재 언어로 생성 (value 는 24h 고정 = 로직/매칭 기준)
const buildVisitTimeOptions = () => [
    { value: '06:00', label: i18n.t('subPlace.timeAm', { h: 6 }) },
    { value: '07:00', label: i18n.t('subPlace.timeAm', { h: 7 }) },
    { value: '08:00', label: i18n.t('subPlace.timeAm', { h: 8 }) },
    { value: '09:00', label: i18n.t('subPlace.timeAm', { h: 9 }) },
    { value: '10:00', label: i18n.t('subPlace.timeAm', { h: 10 }) },
    { value: '11:00', label: i18n.t('subPlace.timeAm', { h: 11 }) },
    { value: '12:00', label: i18n.t('subPlace.timeNoon') },
    { value: '13:00', label: i18n.t('subPlace.timePm', { h: 1 }) },
    { value: '14:00', label: i18n.t('subPlace.timePm', { h: 2 }) },
    { value: '15:00', label: i18n.t('subPlace.timePm', { h: 3 }) },
    { value: '16:00', label: i18n.t('subPlace.timePm', { h: 4 }) },
    { value: '17:00', label: i18n.t('subPlace.timePm', { h: 5 }) },
    { value: '18:00', label: i18n.t('subPlace.timePm', { h: 6 }) },
    { value: '19:00', label: i18n.t('subPlace.timePm', { h: 7 }) },
    { value: '20:00', label: i18n.t('subPlace.timePm', { h: 8 }) },
    { value: '21:00', label: i18n.t('subPlace.timePm', { h: 9 }) },
    { value: '22:00', label: i18n.t('subPlace.timePm', { h: 10 }) },
];

// Category labels
const getCategoryLabel = (category: string) => {
    const keys: Record<string, string> = {
        'PRIMARY': 'category.primary',
        'SECONDARY': 'category.secondary',
        'TERTIARY': 'category.tertiary',
        'ATTRACTION': 'category.attraction',
        'RESTAURANT': 'category.restaurant',
        'CAFE': 'category.cafe',
        'HOTEL': 'category.hotel',
    };
    return keys[category] ? i18n.t(keys[category]) : category;
};

// Category icons (SVG) — 이모지 금지. 1차/2차/3차는 매핑 아이콘이 없어 숫자 텍스트로 표기.
// PRIMARY/SECONDARY/TERTIARY 의 순번 텍스트
const CATEGORY_ORDINAL: Record<string, string> = {
    PRIMARY: '1',
    SECONDARY: '2',
    TERTIARY: '3',
};
const CATEGORY_ICON_COMP: Record<string, React.FC<IconProps>> = {
    ATTRACTION: IcLandmark,
    RESTAURANT: IcRestaurant,
    CAFE: IcCafe,
    HOTEL: IcHotel,
};

// JSX element 형태로 카테고리 아이콘 반환 — 모든 렌더 사이트에서 사용.
// 1·2·3차는 인라인 숫자, 그 외는 SVG 컴포넌트.
const getCategoryIconEl = (category: string, size = 16): React.ReactNode => {
    const ordinal = CATEGORY_ORDINAL[category];
    if (ordinal) {
        return <span style={{ fontWeight: 700, fontSize: Math.round(size * 0.85) }}>{getCategoryLabel(category)}</span>;
    }
    const Comp = CATEGORY_ICON_COMP[category] || IcPin;
    return <Comp size={size} />;
};

// ─── rev3 · RecommendPreview 인라인 위젯 ───
// 시트 안에서 상위 5개 추천을 가로 스크롤 카드로 노출 · 클릭 시 즉시 폼 채움.
// 사진은 PlaceExplore 와 동일한 흐름 재사용:
//   1) nearby 응답의 photoUrl 즉시 사용 (일본=Hotpepper/OTM 등)
//   2) 4sqi.net/img/categories 는 카테고리 아이콘이라 사진 아님 → 제외
//   3) 그 외엔 placeholder 아이콘
interface RecommendPickResult {
    name: string;
    address: string;
    lat: number;
    lng: number;
    placeId?: string;
}

// 사진 없는 항목만 골라 한 번에 채웁니다. 요청·정리는 common/utils/placePhotos 가 합니다.
async function loadPhotosBatch(items: any[]): Promise<Record<string, string>> {
    const noPhotoIds = items
        .filter((p) => !pickPhotoUrl(p) && p?.placeId)
        .map((p) => String(p.placeId))
        .slice(0, 20);
    const photos = await fetchPlacePhotos(noPhotoIds);
    const out: Record<string, string> = {};
    for (const [id, pd] of Object.entries(photos)) {
        if (pd.photoUrl) out[id] = pd.photoUrl;
    }
    return out;
}

// ─── rev3+ · RecommendMapSheet ───
// 카드 클릭 시 열리는 지도 시트 — PlaceMapView 로 지도 + 마커 + 지도 이동 시 재검색.
// 실제 앱(카카오/네이버맵)의 "주변 장소" UI 를 그대로 재현.
interface RecommendMapSheetProps {
    isOpen: boolean;
    onClose: () => void;
    initialCenter: { lat: number; lng: number } | null;
    initialSelectedId?: string | null;
    typeConfigs: Array<{ type: string; limit: number }>;
    title: string;
    confirmLabel: string;
    onConfirm: (pick: RecommendPickResult) => void;
    /** rev4 · 지도 마커 스타일 mode — simple(일반) / attraction(관광지) / hotel(숙박) */
    markerMode?: 'simple' | 'attraction' | 'hotel';
}

const nearbyToMapPlace = (p: any, type: string): MapPlace => ({
    id: `${p.placeId || p.name}-${type}`,
    /* 사진 조회 키. id 는 뒤에 type 을 붙여 쓰므로 쪼개 복원하면 안 됩니다 —
       카카오 placeId 자체가 'kakao-12089662' 라 'kakao' 로 잘렸습니다. */
    placeId: p.placeId || undefined,
    name: p.name,
    address: p.address || '',
    category: type,
    categoryLabel: p.categoryLabel || type,
    lat: Number(p.lat) || 0,
    lng: Number(p.lng) || 0,
    imageUrl: pickPhotoUrl(p),
    rating: p.rating || undefined,
    reviewCount: p.userRatingsTotal || 0,
});

// rev4 · mode 별 chip 카테고리 목록
const CATEGORY_SET_HOTEL = [
    { id: 'all', label: '전체', icon: 'hotel' },
    { id: 'hotel', label: '호텔', icon: 'hotel' },
    { id: 'lodging', label: '료칸', icon: 'hotel' },
    { id: 'accommodation', label: '게하', icon: 'hotel' },
];
const CATEGORY_SET_ATTRACTION = [
    { id: 'all', label: '전체', icon: 'attraction' },
    { id: 'attraction', label: '명소', icon: 'attraction' },
    { id: 'restaurant', label: '식당', icon: 'restaurant' },
    { id: 'cafe', label: '카페', icon: 'cafe' },
    { id: 'shopping', label: '쇼핑', icon: 'shopping' },
];
const CATEGORY_SET_SIMPLE = [
    { id: 'all', label: '전체', icon: 'pin' },
    { id: 'restaurant', label: '식당', icon: 'restaurant' },
    { id: 'cafe', label: '카페', icon: 'cafe' },
    { id: 'bar', label: '술집', icon: 'bar' },
    { id: 'entertainment', label: '문화', icon: 'entertainment' },
];

const RecommendMapSheet: React.FC<RecommendMapSheetProps> = ({
    isOpen, onClose, initialCenter, initialSelectedId, typeConfigs, title, confirmLabel, onConfirm,
    markerMode = 'simple',
}) => {
    const { t } = useTranslation();
    const [center, setCenter] = useState<{ lat: number; lng: number } | null>(initialCenter);
    const [places, setPlaces] = useState<MapPlace[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId || null);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    // rev4 · 3단계 시트 stage — handle 클릭 시 peek → half → full → peek 순환
    const [sheetStage, setSheetStage] = useState<'peek' | 'half' | 'full'>('half');
    const cycleSheetStage = useCallback(() => {
        setSheetStage((prev) => (prev === 'peek' ? 'half' : prev === 'half' ? 'full' : 'peek'));
    }, []);

    // 현재 mode 에 맞는 chip 목록
    const chipCategories = markerMode === 'hotel'
        ? CATEGORY_SET_HOTEL
        : markerMode === 'attraction'
            ? CATEGORY_SET_ATTRACTION
            : CATEGORY_SET_SIMPLE;

    // chip 선택 시 typeConfigs 는 자동으로 재계산 → nearby 재호출
    const activeTypeConfigs = useMemo(() => {
        if (selectedCategory === 'all') return typeConfigs;
        return [{ type: selectedCategory, limit: typeConfigs[0]?.limit || 25 }];
    }, [selectedCategory, typeConfigs]);

    // isOpen 이 true 로 바뀔 때 initialCenter 로 재세팅 (다음 오픈에서 이전 상태 잔존 방지)
    useEffect(() => {
        if (isOpen) {
            setCenter(initialCenter);
            setSelectedId(initialSelectedId || null);
            setPlaces([]);
        }
    }, [isOpen, initialCenter, initialSelectedId]);

    // center 바뀔 때마다 nearby 재조회 (지도 드래그 → 새 좌표 주변 추천 자동 재검색)
    //   chip 선택도 activeTypeConfigs 재계산해서 nearby 재호출 트리거
    useEffect(() => {
        if (!isOpen || !center) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const results = await Promise.allSettled(
                    activeTypeConfigs.map(({ type, limit }) =>
                        instance.get(getApiUrl('nearbyPlaces'), {
                            params: { lat: center.lat, lng: center.lng, type, radius: 5000, limit },
                        }).then((res) => ({ type, data: res?.data?.data || [] }))
                    )
                );
                if (cancelled) return;
                const all: MapPlace[] = [];
                const raw: any[] = [];
                const seen = new Set<string>();
                for (const r of results) {
                    if (r.status !== 'fulfilled') continue;
                    for (const p of r.value.data) {
                        if (!p?.name) continue;
                        const key = p.placeId || p.name;
                        if (seen.has(key)) continue;
                        seen.add(key);
                        raw.push({ ...p, __type: r.value.type });
                        all.push(nearbyToMapPlace(p, r.value.type));
                    }
                }
                if (cancelled) return;
                const capped = all.slice(0, 30);
                setPlaces(capped);
                // initialSelectedId 는 raw placeId 로 넘어오는데 MapPlace.id 는 `${placeId}-${type}` 형식.
                // prefix match 로 재매핑해서 setSelectedId — PlaceMapView 가 자동으로 마커 상단 배치 + detail 팝업 오픈
                if (initialSelectedId) {
                    const idStr = String(initialSelectedId);
                    const match = capped.find((mp) => mp.id === idStr || mp.id.startsWith(`${idStr}-`) || mp.name === idStr);
                    if (match) setSelectedId(match.id);
                }
                // 사진 없는 항목들 → placePhotos API 로 지연 로드
                const photoMap = await loadPhotosBatch(raw.slice(0, 30));
                if (cancelled) return;
                if (Object.keys(photoMap).length > 0) {
                    setPlaces((prev) => prev.map((mp) => {
                        const url = mp.placeId ? photoMap[mp.placeId] : undefined;
                        return url && !mp.imageUrl ? { ...mp, imageUrl: url } : mp;
                    }));
                }
            } catch {
                if (!cancelled) setPlaces([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, center?.lat, center?.lng, JSON.stringify(activeTypeConfigs)]); // eslint-disable-line react-hooks/exhaustive-deps

    /* 누르기 시작한 곳도 배경일 때만 닫습니다 — 누르는 사이 화면이
       움직이면 내용 위에서 시작한 탭이 배경에서 끝나 창이 닫힙니다. */
    const mapBackdrop = useBackdropClose<HTMLDivElement>(onClose);

    useBackDismiss(isOpen, onClose);

    if (!isOpen || !center) return null;
    const portalRoot = document.getElementById('portal') || document.body;
    return ReactDOM.createPortal(
        <div className={cls("v6-map-sheet-overlay")} {...mapBackdrop}>
            <div className={cls(`v6-map-sheet stage-${sheetStage}`)} onClick={(e) => e.stopPropagation()}>
                <div
                    className={cls("v6-map-sheet-handle-wrap")}
                    onClick={(e) => { e.stopPropagation(); cycleSheetStage(); }}
                    title={sheetStage === 'peek' ? '반쯤 열기' : sheetStage === 'half' ? '전체 열기' : '최소화'}
                >
                    <div className={cls("v6-map-sheet-handle")} />
                </div>
                <div className={cls("v6-map-sheet-header")}>
                    <span className={cls("v6-map-sheet-title")}>{title}</span>
                    <button type="button" className={cls("v6-map-sheet-close")} onClick={onClose} aria-label="close">
                        <IcClose size={16} />
                    </button>
                </div>
                <div className={cls("v6-map-sheet-body")}>
                    <PlaceMapView
                        places={places}
                        center={center}
                        selectedPlaceId={selectedId}
                        categories={chipCategories}
                        selectedCategory={selectedCategory}
                        onCategoryChange={setSelectedCategory}
                        onPlaceSelect={(p) => setSelectedId(p.id)}
                        onPlaceConfirm={(p) => onConfirm({
                            name: p.name,
                            address: p.address || '',
                            lat: p.lat,
                            lng: p.lng,
                            placeId: (p as any).placeId || p.id,
                        })}
                        onCenterChange={(c) => setCenter(c)}
                        showBackButton={false}
                        showSearch={false}
                        showCategories={true}
                        confirmButtonText={confirmLabel}
                        height="100%"
                        markerMode={markerMode}
                    />
                    {loading ? (
                        <div style={{
                            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                            background: 'var(--t-surface)', border: '1px solid var(--t-line)',
                            borderRadius: 999, padding: '4px 12px', fontSize: 11, color: 'var(--t-ink-3)', zIndex: 100,
                        }}>
                            {t('subPlace.mapSheetLoading')}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>,
        portalRoot
    );
};

interface RecommendPreviewProps {
    anchor: { lat: number; lng: number } | null;
    typeConfigs: Array<{ type: string; limit: number }>;
    onPick: (place: RecommendPickResult) => void;
    labelKey?: string;
    labelDefault?: string;
}

// rev5 이후 RecommendSections 로 대체됨(모드별 3섹션). 여행/숙박 인라인 프리뷰가 필요할 때를 대비해 보존.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RecommendPreview: React.FC<RecommendPreviewProps> = ({ anchor, typeConfigs, onPick, labelKey, labelDefault }) => {
    const { t } = useTranslation();
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const cfgKey = JSON.stringify(typeConfigs);
    useEffect(() => {
        if (!anchor || !typeConfigs.length) {
            setItems([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const results = await Promise.allSettled(
                    typeConfigs.map(({ type, limit }) =>
                        instance.get(getApiUrl('nearbyPlaces'), {
                            params: { lat: anchor.lat, lng: anchor.lng, type, radius: 5000, limit },
                        }).then((res) => res?.data?.data || [])
                    )
                );
                if (cancelled) return;
                const all: any[] = [];
                for (const r of results) {
                    if (r.status === 'fulfilled') all.push(...r.value);
                }
                const deduped = Array.from(new Map(all.filter((p) => p?.name).map((p) => [p.name, p])).values()).slice(0, 5);
                if (cancelled) return;
                setItems(deduped);
                // photoUrl 없는 항목들 → placePhotos API 로 지연 로드
                const photoMap = await loadPhotosBatch(deduped);
                if (cancelled) return;
                if (Object.keys(photoMap).length > 0) {
                    setItems((prev) => prev.map((p) => {
                        const pid = String(p.placeId || '');
                        return photoMap[pid] ? { ...p, photoUrl: photoMap[pid] } : p;
                    }));
                }
            } catch {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [anchor?.lat, anchor?.lng, cfgKey]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!anchor) return null;
    if (loading) {
        return (
            <div className={cls("recommend-preview-wrap")}>
                <div className={cls("recommend-preview-label")}>
                    <span>{t(labelKey || 'subPlace.recommendPreviewLabel', { defaultValue: labelDefault })}</span>
                </div>
                <div className={cls("recommend-preview-loading")}>
                    {t('subPlace.recommendPreviewLoading')}
                </div>
            </div>
        );
    }
    if (items.length === 0) return null;
    return (
        <div className={cls("recommend-preview-wrap")}>
            <div className={cls("recommend-preview-label")}>
                <span>{t(labelKey || 'subPlace.recommendPreviewLabel', { defaultValue: labelDefault })}</span>
                <span className={cls("tag")}>Top {items.length}</span>
            </div>
            <div className={cls("recommend-preview-scroll")}>
                {items.map((p, i) => {
                    const photo = pickPhotoUrl(p);
                    return (
                        <button
                            key={`${p.placeId || p.name}-${i}`}
                            type="button"
                            className={cls("recommend-preview-card")}
                            onClick={() => onPick({
                                name: p.name,
                                address: p.address || '',
                                lat: Number(p.lat) || 0,
                                lng: Number(p.lng) || 0,
                                placeId: p.placeId,
                            })}
                        >
                            <div
                                className={cls(`recommend-preview-thumb ${photo ? '' : 'no-image'}`)}
                                style={photo ? { backgroundImage: `url(${photo})` } : undefined}
                            >
                                {photo ? null : <IcHotel size={22} />}
                            </div>
                            <div className={cls("recommend-preview-body")}>
                                <span className={cls("rec-name")}>{p.name}</span>
                                <span className={cls("rec-meta")}>
                                    {p.categoryLabel || p.category || ''}
                                    {p.rating ? ` · ${p.rating}★` : ''}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// ─── rev5-A · 일반모임 추천 3섹션 리스트 ───
// 섹션: ① 최근 방문한 곳(내 이력) · ② 참여자가 자주 간 곳(모임 기준) · ③ 내 주변 인기(nearby)
// 카드 마크업·CSS(recommend-preview-*)·loadPhotosBatch 재사용. 데이터 없는 섹션은 렌더 안 함.
type RecommendMode = 'general' | 'korea' | 'overseas';

// 섹션 데이터 소스 정의 — recent(내 이력) / frequent(모임) / nearby(주변, type별)
type SectionSource =
    | { kind: 'recent'; filterNear?: boolean }
    | { kind: 'frequent' }
    | { kind: 'nearby'; types: string[]; limit?: number };
/* icon — 분류를 한눈에 가르는 표식. 제목만으로는 세 줄이 다 똑같아 보입니다. */
interface SectionCfg { key: string; label: string; icon: React.FC<IconProps>; source: SectionSource; }

interface RecommendSectionsProps {
    mode: RecommendMode;
    anchor: { lat: number; lng: number } | null;
    nearbyTypeConfigs: Array<{ type: string; limit: number }>;
    meetingId?: string;
    nearbyLabel: string;
    onPick: (place: RecommendPickResult) => void;
    /** 「더 보기」 — 자세히 고르는 건 추천 화면에서 */
    onMore: () => void;
}

/** 창 안에서는 맛보기만 — 이만큼만 보여 주고 나머지는 「더 보기」로 */
const PREVIEW_COUNT = 6;

// "최근 이 여행지" 필터 반경(km) — recent 이력을 여행지 근처로 한정 (rev5-D 회귀 방지: 엉뚱한 지역 recent 제외)
const RECENT_NEAR_KM = 80;

// 두 좌표 간 거리(km) — 카드 거리 라벨용 (haversine)
const getDistanceKm = (lat1: number, lng1: number, lat2: number, lng2: number): number =>
    haversineMeters(lat1, lng1, lat2, lng2) / 1000;

/* 좌표 한 개를 숫자로.
   서버는 latitude 를 **문자열**로 돌려줍니다("37.2281533"). typeof 로
   number 만 받으면 API 에서 온 좌표가 전부 버려집니다 — 장소를 고칠 때
   좌표가 빠져 좌표 없는 place 가 새로 생기고, 지도 anchor 도 안 잡혔습니다. */
const coordNum = (v: unknown): number | undefined => {
    const n = Number(v);
    return Number.isFinite(n) && n !== 0 ? n : undefined;
};

// 다양한 응답(최근=placeName/latitude, nearby=name/lat)을 공통 카드 형태로 정규화
const normalizeSectionPlace = (p: any): any => ({
    name: p.name || p.placeName || '',
    address: p.address || p.placeAddress || '',
    lat: Number(p.lat ?? p.latitude) || 0,
    lng: Number(p.lng ?? p.longitude) || 0,
    // 사진 배치 조회 키 — 추천장소 매칭용 externalPlaceId 우선
    placeId: p.externalPlaceId || p.placeId || undefined,
    photoUrl: p.photoUrl || p.imageUrl || undefined,
    categoryLabel: p.categoryLabel || p.category || '',
    rating: p.rating || undefined,
    reviewCount: typeof p.userRatingsTotal === 'number' ? p.userRatingsTotal
        : (typeof p.reviewCount === 'number' ? p.reviewCount : undefined),
    visitCount: typeof p.visitCount === 'number' ? p.visitCount : undefined,
});

const RecommendSections: React.FC<RecommendSectionsProps> = ({ mode, anchor, nearbyTypeConfigs, meetingId, nearbyLabel, onPick, onMore }) => {
    const { t } = useTranslation();
    const [data, setData] = useState<Record<string, any[]>>({});

    // 모드별 섹션 구성 (rev5 § 03 매트릭스) — general/korea/overseas
    const sections: SectionCfg[] = useMemo(() => {
        /* 창 안에서는 「다시 갈 만한 곳」 한 줄만 둡니다.
           명소·맛집·숙소 둘러보기는 「추천 장소에서 찾기」가 분류별로 보여 주므로,
           여기서 또 길게 늘어놓으면 정작 고르는 수단이 묻힙니다. */
        if (mode === 'korea') return [
            { key: 'recentDest', label: t('subPlace.secRecentDest'), icon: IcClock, source: { kind: 'recent', filterNear: true } },
        ];
        if (mode === 'overseas') return [
            { key: 'recentReserve', label: t('subPlace.secRecentReserve'), icon: IcClock, source: { kind: 'recent', filterNear: true } },
        ];
        // general
        return [
            { key: 'recent', label: t('subPlace.sectionRecent'), icon: IcClock, source: { kind: 'recent' } },
        ];
    }, [mode, t]);

    // 사진 없는 항목 → placePhotos 배치 지연 로드 (해당 섹션만 갱신)
    const enrichPhotosFor = useCallback(async (items: any[], key: string) => {
        const photoMap = await loadPhotosBatch(items);
        if (Object.keys(photoMap).length === 0) return;
        setData((prev) => ({
            ...prev,
            [key]: (prev[key] || items).map((p) => {
                const pid = String(p.placeId || '');
                return !pickPhotoUrl(p) && photoMap[pid] ? { ...p, photoUrl: photoMap[pid] } : p;
            }),
        }));
    }, []);

    const anchorKey = anchor ? `${anchor.lat},${anchor.lng}` : '';
    const secKey = JSON.stringify(sections.map((s) => ({ k: s.key, s: s.source })));
    useEffect(() => {
        let cancelled = false;

        const loadRecent = async (filterNear?: boolean) => {
            const res = await instance.get(`${getApiUrl('meetings')}/places/recent`, { params: { limit: 15 } });
            let list = (res?.data?.data || []).map(normalizeSectionPlace).filter((p: any) => p.name);
            if (filterNear && anchor) {
                list = list.filter((p: any) => p.lat && p.lng && getDistanceKm(anchor.lat, anchor.lng, p.lat, p.lng) <= RECENT_NEAR_KM);
            }
            return list.slice(0, 12);
        };
        const loadFrequent = async () => {
            if (!meetingId) return [];
            const res = await instance.get(`${getApiUrl('meetingDetail')}/${meetingId}/frequent-places`, { params: { limit: 10 } });
            return (res?.data?.data || []).map(normalizeSectionPlace).filter((p: any) => p.name);
        };
        const loadNearby = async (types: string[], limit: number) => {
            if (!anchor) return [];
            const results = await Promise.allSettled(
                types.map((type) =>
                    instance.get(getApiUrl('nearbyPlaces'), {
                        params: { lat: anchor.lat, lng: anchor.lng, type, radius: 5000, limit },
                    }).then((res) => res?.data?.data || [])
                )
            );
            const all: any[] = [];
            for (const r of results) if (r.status === 'fulfilled') all.push(...r.value);
            return Array.from(new Map(all.filter((p) => p?.name).map((p) => [p.name, p])).values())
                .slice(0, 12).map(normalizeSectionPlace);
        };

        sections.forEach(async (sec) => {
            let items: any[] = [];
            try {
                if (sec.source.kind === 'recent') items = await loadRecent(sec.source.filterNear);
                else if (sec.source.kind === 'frequent') items = await loadFrequent();
                else items = await loadNearby(sec.source.types, sec.source.limit || 15);
            } catch { items = []; }
            if (cancelled) return;
            setData((prev) => ({ ...prev, [sec.key]: items }));
            if (items.length > 0) enrichPhotosFor(items, sec.key);
        });

        return () => { cancelled = true; };
    }, [secKey, anchorKey, meetingId, enrichPhotosFor]); // eslint-disable-line react-hooks/exhaustive-deps

    const renderCard = (p: any, i: number) => {
        const photo = pickPhotoUrl(p);
        const distKm = anchor && p.lat && p.lng ? getDistanceKm(anchor.lat, anchor.lng, p.lat, p.lng) : null;
        const distLabel = distKm == null ? '' : distKm < 1 ? `${Math.round(distKm * 1000)}m` : `${distKm.toFixed(1)}km`;
        // 실제 필드만 노출 (가격·하트는 데이터 없어 미표시)
        const meta = [
            p.categoryLabel || '',
            p.rating ? `${p.rating}★` : '',
            p.reviewCount ? t('subPlace.reviewCount', { count: p.reviewCount }) : '',
            distLabel,
        ].filter(Boolean).join(' · ');
        return (
            <button
                key={`${p.placeId || p.name}-${i}`}
                type="button"
                className={cls("recommend-preview-card")}
                onClick={() => onPick({ name: p.name, address: p.address, lat: p.lat, lng: p.lng, placeId: p.placeId })}
            >
                <div
                    className={cls(`recommend-preview-thumb ${photo ? '' : 'no-image'}`)}
                    style={photo ? { backgroundImage: `url(${photo})` } : undefined}
                >
                    {photo ? null : <IcHotel size={22} />}
                </div>
                <div className={cls("recommend-preview-body")}>
                    <span className={cls("rec-name")}>{p.name}</span>
                    <span className={cls("rec-meta")}>{meta}</span>
                </div>
            </button>
        );
    };

    const anyItems = sections.some((s) => (data[s.key] || []).length > 0);
    if (!anchor && !anyItems) return null;

    return (
        <div className={cls(`recommend-sections recommend-sections--${mode}`)}>
            {sections.map((sec) => {
                const items = data[sec.key] || [];
                if (items.length === 0) return null;
                const SecIcon = sec.icon;
                return (
                    <div className={cls("recommend-preview-wrap")} key={sec.key}>
                        <div className={cls("recommend-preview-label")}>
                            <span className={cls("rec-sec-icon")}><SecIcon size={15} /></span>
                            <span className={cls("rec-sec-title")}>{sec.label}</span>
                            {/* 자세히 고르는 건 한 단계 들어가서 — 창을 추천으로 가득 채우지 않습니다 */}
                            <button type="button" className={cls("rec-sec-more")} onClick={onMore}>
                                {t('common.more')}
                                <span aria-hidden>›</span>
                            </button>
                        </div>
                        <div className={cls("recommend-preview-scroll")}>
                            {items.slice(0, PREVIEW_COUNT).map(renderCard)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export const SubPlaceModal = ({
    isOpen,
    onClose,
    meetingId,
    editingSubPlace,
    isTravelMeeting,
    travelDestinationName,
    travelDestinationCenter,
    travelPeriod,
    meetingTime,
    subPlaces,
    onSaveSuccess,
    returnTo,
    initialForm,
    initialIsCustomVisitTime,
    meetingScheduleStartDatetime,
    meetingScheduleEndDatetime,
    meetingScheduleId,
    mainPlaceName,
    editingMainPlace,
    onSaveMainPlace,
    lockCategoryToHotel = false,
    hideHotelInCategoryPicker = false,
}: SubPlaceModalProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert, showConfirm } = useAlert();

    // 현재 위치 프리페치 — 모달 열릴 때 미리 받아두면 지도 열 때 캐시 히트로 즉시 뜬다.
    //   (지도 켤 때 위치 조회하면 네이티브 3s/브라우저 최대 8s 대기 → 체감 지연.)
    const { fetchLocation } = useUserLocation();
    useEffect(() => {
        if (isOpen) { void fetchLocation(); }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const defaultForm: SubPlaceForm = {
        placeName: '',
        placeAddress: '',
        category: isTravelMeeting ? 'ATTRACTION' : 'PRIMARY',
        dayNumber: 1,
        dayNumbers: [1],
        visitTime: '',
        description: ''
    };

    const [subPlaceForm, setSubPlaceForm] = useState<SubPlaceForm>(initialForm || defaultForm);
    const [isCustomVisitTime, setIsCustomVisitTime] = useState(initialIsCustomVisitTime || false);

    // rev3+ / rev4 · 추천 지도 시트 상태
    const [mapSheetState, setMapSheetState] = useState<{
        open: boolean;
        center: { lat: number; lng: number };
        selectedId?: string | null;
        typeConfigs: Array<{ type: string; limit: number }>;
        title: string;
        markerMode: 'simple' | 'attraction' | 'hotel';
        initialPick: RecommendPickResult | null;
    } | null>(null);

    // datetime-local input 'YYYY-MM-DDTHH:mm' (로컬 타임존, 초 제외)
    const toLocalInput = (iso: string | null | undefined): string => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    // 새로 추가하는 sub_place 의 디폴트 시작/종료 — 모임 schedule.endDatetime 이 있으면 둘 다 그 값.
    // (사용자 사양: 디폴트 종료 = 모임 종료시간. 사용자가 종료를 늘리면 모임 종료도 자동 연장)
    const defaultRangeForNewSubPlace = (): { start: string; end: string } => {
        const ms = meetingScheduleEndDatetime ? new Date(meetingScheduleEndDatetime) : null;
        if (!ms || isNaN(ms.getTime())) return { start: '', end: '' };
        const v = toLocalInput(ms.toISOString());
        return { start: v, end: v };
    };

    // 본 장소 편집 모드 — schedule 시작/종료 가 datetime 디폴트
    const isMainPlaceEdit = !!editingMainPlace;

    // Reset form when modal opens or editing changes
    useEffect(() => {
        if (isOpen) {
            if (isMainPlaceEdit && editingMainPlace) {
                // 본 장소 편집: schedule 시작/종료 를 datetime-local 로 prefill
                setSubPlaceForm({
                    placeName: editingMainPlace.placeName || '',
                    placeAddress: editingMainPlace.placeAddress || '',
                    category: 'PRIMARY',
                    dayNumber: 1,
                    dayNumbers: [1],
                    visitTime: '',
                    description: '',
                    startDatetimeLocal: toLocalInput(meetingScheduleStartDatetime || undefined),
                    endDatetimeLocal: toLocalInput(meetingScheduleEndDatetime || undefined),
                    ...(coordNum(editingMainPlace.latitude) !== undefined
                        ? { latitude: coordNum(editingMainPlace.latitude) } : {}),
                    ...(coordNum(editingMainPlace.longitude) !== undefined
                        ? { longitude: coordNum(editingMainPlace.longitude) } : {}),
                });
                setIsCustomVisitTime(false);
            } else if (initialForm) {
                // 호출자가 datetime 디폴트를 안 채웠고 추가 모드면 모임 종료 시각으로 자동 채움
                const useNewDayInputs = !isTravelMeeting && initialForm.category !== 'HOTEL' && !!meetingScheduleEndDatetime;
                if (useNewDayInputs && !initialForm.startDatetimeLocal && !initialForm.endDatetimeLocal) {
                    const def = defaultRangeForNewSubPlace();
                    setSubPlaceForm({ ...initialForm, startDatetimeLocal: def.start, endDatetimeLocal: def.end });
                } else {
                    setSubPlaceForm(initialForm);
                }
                setIsCustomVisitTime(initialIsCustomVisitTime || false);
            } else if (editingSubPlace) {
                const visitTime = editingSubPlace.visitTime || '';
                const isPreset = buildVisitTimeOptions().some(opt => opt.value === visitTime);
                const startIso = (editingSubPlace as any).startDatetime as string | undefined;
                const endIso = (editingSubPlace as any).endDatetime as string | undefined;
                const splitIso = (iso?: string) => {
                    if (!iso) return { date: '', time: '' };
                    const d = new Date(iso);
                    if (isNaN(d.getTime())) return { date: '', time: '' };
                    const pad = (n: number) => String(n).padStart(2, '0');
                    return {
                        date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
                        time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
                    };
                };
                const ci = splitIso(startIso);
                const co = splitIso(endIso);
                // DAY 비-HOTEL 편집: 기존 sub_place 가 start/end 없이 생성된 경우 (legacy visitTime 만 있는 데이터)
                // 디폴트로 모임 종료 시각을 채워주어 저장 시 'datetime 비어있음' 에러를 회피.
                const isDayNonHotel = !isTravelMeeting && editingSubPlace.category !== 'HOTEL';
                let startLocal = isDayNonHotel ? toLocalInput(startIso) : undefined;
                let endLocal = isDayNonHotel ? toLocalInput(endIso) : undefined;
                if (isDayNonHotel && !startLocal && !endLocal && meetingScheduleEndDatetime) {
                    const def = defaultRangeForNewSubPlace();
                    startLocal = def.start;
                    endLocal = def.end;
                }
                setSubPlaceForm({
                    placeName: editingSubPlace.placeName,
                    placeAddress: editingSubPlace.placeAddress || '',
                    category: editingSubPlace.category,
                    dayNumber: editingSubPlace.dayNumber || 1,
                    dayNumbers: [editingSubPlace.dayNumber || 1],
                    visitTime: visitTime,
                    description: editingSubPlace.description || '',
                    checkInDate: ci.date,
                    checkInTime: ci.time,
                    checkOutDate: co.date,
                    checkOutTime: co.time,
                    startDatetimeLocal: startLocal,
                    endDatetimeLocal: endLocal,
                    // 기존 좌표 보존 — 단순 메모 수정 시에도 좌표가 빠지지 않도록 form 에 복원
                    ...(coordNum(editingSubPlace.latitude) !== undefined
                        ? { latitude: coordNum(editingSubPlace.latitude) } : {}),
                    ...(coordNum(editingSubPlace.longitude) !== undefined
                        ? { longitude: coordNum(editingSubPlace.longitude) } : {}),
                    ...(editingSubPlace.placeId ? { placeId: editingSubPlace.placeId } : {}),
                });
                setIsCustomVisitTime(visitTime !== '' && !isPreset);
            } else {
                const def = defaultForm.category !== 'HOTEL' && !isTravelMeeting && meetingScheduleEndDatetime
                    ? defaultRangeForNewSubPlace() : { start: '', end: '' };
                setSubPlaceForm({ ...defaultForm, startDatetimeLocal: def.start, endDatetimeLocal: def.end });
                setIsCustomVisitTime(false);
            }
        }
    }, [isOpen, editingSubPlace, initialForm, initialIsCustomVisitTime, isTravelMeeting, meetingScheduleEndDatetime, isMainPlaceEdit, editingMainPlace, meetingScheduleStartDatetime]);

    // 여행 모임 신규 장소: 기본 선택 일차가 이미 지난 날이면 첫 유효(오늘/미래) 일차로 보정 + 과거일 제거.
    // (지난 일차는 칩이 비활성화되므로 기본값이 거기 머물면 선택을 못 바꿈)
    useEffect(() => {
        if (!isOpen || editingSubPlace || isMainPlaceEdit) return;
        if (!isTravelMeeting || !travelPeriod || travelPeriod.days <= 1) return;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isPastDay = (d: number) => {
            const dd = new Date(travelPeriod.start);
            dd.setDate(dd.getDate() + (d - 1));
            dd.setHours(0, 0, 0, 0);
            return dd.getTime() < today.getTime();
        };
        let firstValid = 0;
        for (let d = 1; d <= travelPeriod.days; d++) {
            if (!isPastDay(d)) { firstValid = d; break; }
        }
        if (!firstValid) return; // 전 일정이 과거면 그대로 둠
        setSubPlaceForm(prev => {
            const cur = (prev.dayNumbers && prev.dayNumbers.length) ? prev.dayNumbers : [prev.dayNumber];
            const valid = cur.filter(d => !isPastDay(d));
            if (valid.length === cur.length && valid.length > 0) return prev; // 이미 모두 유효
            return { ...prev, dayNumber: valid[0] ?? firstValid, dayNumbers: valid.length ? valid : [firstValid] };
        });
    }, [isOpen, editingSubPlace, isMainPlaceEdit, isTravelMeeting, travelPeriod]);

    // Filter visit time options based on meeting time
    const visitTimeOptions = useMemo(() => {
        const meetingMinutes = parseTimeToMinutes(meetingTime || '');
        if (meetingMinutes === null) return buildVisitTimeOptions();
        return buildVisitTimeOptions().filter(option => {
            const optionMinutes = parseTimeToMinutes(option.value);
            return optionMinutes !== null && optionMinutes >= meetingMinutes;
        });
    }, [meetingTime]);

    // DAY 모임 비-HOTEL 에서 시작/종료 datetime UI 사용 여부 (본 장소 편집은 항상 datetime 사용)
    const useDayDateTimeInputs = isMainPlaceEdit || (
        !isTravelMeeting
        && subPlaceForm.category !== 'HOTEL'
        && !!meetingScheduleEndDatetime
    );

    // 겹친 sub_place 들의 시간을 newRange 와 안 겹치도록 자동 조정.
    // - 기존이 새것보다 일찍 시작 + 새것 시작 안에서 끝남 → 기존.end 를 new.start 로 단축
    // - 기존이 새것보다 늦게 끝 + 새것 끝 안에서 시작 → 기존.start 를 new.end 로 미룸
    // - 기존이 새것을 완전히 감쌈 → 자동 조정 위험 → 사용자 수동 처리
    // - 새것이 기존을 완전히 감쌈 → 기존 삭제 여부 확인
    // 반환: 모든 조정 성공 시 true, 사용자 취소/위험 케이스에 false
    const autoAdjustConflicts = useCallback(async (
        conflicts: Array<{ name: string; sp: any; start: number; end: number }>,
        newRange: { s: number; e: number },
    ): Promise<boolean> => {
        if (!meetingId) return false;
        // 서버는 시각을 UTC 로 받습니다 (예전에는 여기서 벽시계를 따로 조립했습니다)
        const toIsoSec = (ms: number): string => toUtcISOString(new Date(ms));
        for (const c of conflicts) {
            const sp = c.sp;
            let newSpStart = c.start;
            let newSpEnd = c.end;
            const startsBefore = c.start < newRange.s;
            const endsAfter = c.end > newRange.e;
            if (startsBefore && !endsAfter) {
                newSpEnd = newRange.s;
            } else if (!startsBefore && endsAfter) {
                newSpStart = newRange.e;
            } else if (startsBefore && endsAfter) {
                await showAlert({ message: t('subPlace.wrapConflict', { name: c.name }) });
                return false;
            } else {
                const delOk = await showConfirm({
                    title: t('subPlace.deleteExistingTitle'),
                    message: t('subPlace.containedMsg', { name: c.name }),
                    confirmText: t('subPlace.delete'),
                    cancelText: t('subPlace.cancel'),
                });
                if (!delOk) return false;
                try {
                    const delRes = await MeetingRepository.onDeleteSubPlace(meetingId, sp.subPlaceId);
                    if (!isOkResponse(delRes)) {
                        console.error('[SubPlaceModal] 자동 조정 — 삭제 거부', delRes?.status, delRes?.data);
                        await showAlert({ message: errorMessageOf(delRes) || t('subPlace.deleteExistingFail') });
                        return false;
                    }
                    continue;
                } catch (e) {
                    console.error('[SubPlaceModal] 자동 조정 — 삭제 실패', e);
                    await showAlert({ message: t('subPlace.deleteExistingFail') });
                    return false;
                }
            }
            // truncate / shift — PUT
            try {
                const putRes = await MeetingRepository.onPutUpdateSubPlace(meetingId, sp.subPlaceId, {
                    placeName: sp.placeName,
                    placeAddress: sp.placeAddress,
                    category: sp.category,
                    dayNumber: sp.dayNumber,
                    visitTime: sp.visitTime,
                    startDatetime: toIsoSec(newSpStart),
                    endDatetime: toIsoSec(newSpEnd),
                    description: sp.description,
                });
                if (!isOkResponse(putRes)) {
                    console.error('[SubPlaceModal] 자동 조정 — 시간 갱신 거부', putRes?.status, putRes?.data);
                    await showAlert({ message: errorMessageOf(putRes) || t('subPlace.adjustFail', { name: c.name }) });
                    return false;
                }
            } catch (e) {
                console.error('[SubPlaceModal] 자동 조정 — 시간 갱신 실패', e);
                await showAlert({ message: t('subPlace.adjustFail', { name: c.name }) });
                return false;
            }
        }
        return true;
    }, [meetingId, showAlert, showConfirm]);

    /* 이미 지난 날짜는 고를 수 없게 합니다.
       관광지는 일차 칩이 이미 비활성화되지만 숙박은 일차가 아니라 날짜라서
       칩과 무관하게 과거 날짜를 받고 있었습니다. 서버도 막지만(requireNotPastDay)
       고를 수 없게 하는 편이 먼저입니다 — 눌러 보고 거절당하는 것보다 낫습니다. */
    const todayIso = useMemo(() => {
        const d = new Date();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${m}-${day}`;
    }, []);

    // Handle save
    const handleSave = useCallback(async () => {
        /* onRequest 는 예외를 던지지 않습니다 — 상태를 봐야 실패를 압니다.
           이걸 빼먹어 서버가 거부해도 "추가했어요" 가 뜨고 목록에만 안 나타났습니다. */
        const okOrAlert = async (res: any): Promise<boolean> => {
            if (isOkResponse(res)) return true;
            console.error('[SubPlaceModal] 서버가 거부했습니다', res?.status, res?.data);
            await showAlert({ message: errorMessageOf(res) || t('subPlace.saveFail') });
            return false;
        };
        // [TEMP DEBUG] 장소 추가/수정 흐름 진단용 — 이슈 해결되면 제거
        console.log('[SubPlaceModal] handleSave start', {
            meetingId,
            isMainPlaceEdit,
            useDayDateTimeInputs,
            placeName: subPlaceForm.placeName,
            startDatetimeLocal: subPlaceForm.startDatetimeLocal,
            endDatetimeLocal: subPlaceForm.endDatetimeLocal,
            visitTime: subPlaceForm.visitTime,
            category: subPlaceForm.category,
            meetingScheduleStart: meetingScheduleStartDatetime,
            meetingScheduleEnd: meetingScheduleEndDatetime,
        });

        if (!meetingId || !subPlaceForm.placeName.trim()) {
            console.warn('[SubPlaceModal] BLOCKED — 장소명 비어있음');
            await showAlert({ message: t('subPlace.enterName') });
            return;
        }

        // 본 장소 편집 — datetime 검증 후 부모 콜백으로 위임
        if (isMainPlaceEdit) {
            const sLocal = subPlaceForm.startDatetimeLocal;
            const eLocal = subPlaceForm.endDatetimeLocal;
            if (!sLocal || !eLocal) {
                await showAlert({ message: t('subPlace.enterStartEnd') });
                return;
            }
            const sDate = new Date(sLocal);
            const eDate = new Date(eLocal);
            if (isNaN(sDate.getTime()) || isNaN(eDate.getTime())) {
                await showAlert({ message: t('subPlace.badTimeFormat') });
                return;
            }
            if (sDate >= eDate) {
                console.warn('[SubPlaceModal] BLOCKED — 본 장소 편집: start >= end', { sLocal, eLocal });
                await showAlert({ message: t('subPlace.endAfterStart') });
                return;
            }
            // 기존 sub_place 들과 시간 겹침 검증 — 본 장소 시간이 sub_place 와 겹치면 안 됨
            const newRange = { s: sDate.getTime(), e: eDate.getTime() };
            const overlaps = (a: { s: number; e: number }, b: { s: number; e: number }) => a.s < b.e && b.s < a.e;
            const conflicts: Array<{ name: string; sp: any; start: number; end: number }> = [];
            for (const sp of subPlaces || []) {
                const spStart = (sp as any).startDatetime as string | undefined;
                const spEnd = (sp as any).endDatetime as string | undefined;
                if (!spStart || !spEnd) continue;
                const r = { s: new Date(spStart).getTime(), e: new Date(spEnd).getTime() };
                if (overlaps(newRange, r)) {
                    conflicts.push({ name: sp.placeName || t('subPlace.existingPlace'), sp, start: r.s, end: r.e });
                }
            }
            if (conflicts.length > 0) {
                const names = conflicts.map(c => c.name).join(', ');
                const ok = await showConfirm({
                    title: t('subPlace.overlapTitle'),
                    message: t('subPlace.overlapMainMsg', { names }),
                    confirmText: t('subPlace.autoAdjustChange'),
                    cancelText: t('subPlace.editManually'),
                });
                if (!ok) return; // 모달 유지
                const adjusted = await autoAdjustConflicts(conflicts, newRange);
                if (!adjusted) return; // 자동 조정 실패 — 모달 유지
            }

            // 시작/종료 변경 여부
            const origStart = meetingScheduleStartDatetime ? new Date(meetingScheduleStartDatetime).getTime() : null;
            const origEnd = meetingScheduleEndDatetime ? new Date(meetingScheduleEndDatetime).getTime() : null;
            const scheduleChanged = origStart !== sDate.getTime() || origEnd !== eDate.getTime();

            try {
                await onSaveMainPlace?.({
                    placeName: subPlaceForm.placeName,
                    placeAddress: subPlaceForm.placeAddress,
                    latitude: subPlaceForm.latitude,
                    longitude: subPlaceForm.longitude,
                    startDatetimeLocal: sLocal,
                    endDatetimeLocal: eLocal,
                    scheduleChanged,
                });
                onClose();
                onSaveSuccess();
            } catch (error) {
                console.error('본 장소 저장 실패:', error);
                await showAlert({ message: t('subPlace.saveFail') });
            }
            return;
        }

        // DAY 비-HOTEL: 시작/종료 datetime 검증 + 본 장소/기존 sub_place 와 겹침 검사
        let dayStartIso: string | undefined;
        let dayEndIso: string | undefined;
        let needScheduleExtension = false;
        if (useDayDateTimeInputs) {
            const sLocal = subPlaceForm.startDatetimeLocal;
            const eLocal = subPlaceForm.endDatetimeLocal;
            if (!sLocal || !eLocal) {
                await showAlert({ message: t('subPlace.enterStartEnd') });
                return;
            }
            const sDate = new Date(sLocal);
            const eDate = new Date(eLocal);
            if (isNaN(sDate.getTime()) || isNaN(eDate.getTime())) {
                await showAlert({ message: t('subPlace.badTimeFormat') });
                return;
            }
            if (sDate >= eDate) {
                console.warn('[SubPlaceModal] BLOCKED — sub_place: start >= end', { sLocal, eLocal });
                await showAlert({ message: t('subPlace.endAfterStart') });
                return;
            }

            const newRange = { s: sDate.getTime(), e: eDate.getTime() };
            const overlaps = (a: { s: number; e: number }, b: { s: number; e: number }) => a.s < b.e && b.s < a.e;
            const subConflicts: Array<{ name: string; sp: any; start: number; end: number }> = [];
            let mainConflict: { name: string; start: number; end: number } | null = null;

            if (meetingScheduleStartDatetime && meetingScheduleEndDatetime) {
                const main = {
                    s: new Date(meetingScheduleStartDatetime).getTime(),
                    e: new Date(meetingScheduleEndDatetime).getTime(),
                };
                if (overlaps(newRange, main)) {
                    mainConflict = { name: mainPlaceName || t('subPlace.mainPlaceFallback'), start: main.s, end: main.e };
                }
            }
            for (const sp of subPlaces || []) {
                if (editingSubPlace && sp.subPlaceId === editingSubPlace.subPlaceId) continue;
                const spStart = (sp as any).startDatetime as string | undefined;
                const spEnd = (sp as any).endDatetime as string | undefined;
                if (!spStart || !spEnd) continue;
                const r = { s: new Date(spStart).getTime(), e: new Date(spEnd).getTime() };
                if (overlaps(newRange, r)) {
                    subConflicts.push({ name: sp.placeName || t('subPlace.existingPlace'), sp, start: r.s, end: r.e });
                }
            }
            const totalConflicts = (mainConflict ? 1 : 0) + subConflicts.length;
            if (totalConflicts > 0) {
                console.warn('[SubPlaceModal] CONFLICT', { mainConflict, subConflicts, newRange });
                if (mainConflict && subConflicts.length === 0) {
                    // 본 장소만 겹침 — 본 장소는 자동 조정 X (사용자 직접 수정 권장)
                    await showAlert({
                        message: t('subPlace.mainOverlapMsg', { name: mainConflict.name }),
                    });
                    return; // 모달 유지
                }
                const names = [
                    ...(mainConflict ? [t('subPlace.mainPlaceSuffix', { name: mainConflict.name })] : []),
                    ...subConflicts.map(c => c.name),
                ].join(', ');
                const ok = await showConfirm({
                    title: t('subPlace.overlapTitle'),
                    message: mainConflict
                        ? t('subPlace.overlapSubMainMsg', { names })
                        : t('subPlace.overlapSubMsg', { names }),
                    confirmText: t('subPlace.autoAdjustAdd'),
                    cancelText: t('subPlace.editManually'),
                });
                if (!ok) return; // 모달 유지
                if (mainConflict) {
                    // 본 장소가 겹치는 한 추가 불가
                    await showAlert({ message: t('subPlace.changeMainFirst', { name: mainConflict.name }) });
                    return;
                }
                const adjusted = await autoAdjustConflicts(subConflicts, newRange);
                if (!adjusted) return;
            }

            // 서버는 시각을 UTC 로 받습니다 (sLocal/eLocal 은 로컬 벽시계 입력값)
            dayStartIso = localInputToUtc(sLocal);
            dayEndIso = localInputToUtc(eLocal);
            const schedEndMs = new Date(meetingScheduleEndDatetime!).getTime();
            needScheduleExtension = eDate.getTime() > schedEndMs && !!meetingScheduleId;
        }

        // HOTEL: 체크인/체크아웃 일자 검증 + 일자 기준 겹침 확인
        const isHotel = subPlaceForm.category === 'HOTEL';
        let hotelStartIso: string | undefined;
        let hotelEndIso: string | undefined;
        if (isHotel) {
            if (!subPlaceForm.checkInDate || !subPlaceForm.checkOutDate) {
                await showAlert({ message: t('subPlace.selectCheckInOut') });
                return;
            }
            /* min 은 달력만 제한합니다 — 직접 입력하면 과거도 들어옵니다 */
            if (subPlaceForm.checkInDate < todayIso) {
                await showAlert({ message: t('subPlace.pastDateBlocked') });
                return;
            }
            if (subPlaceForm.checkInDate > subPlaceForm.checkOutDate) {
                await showAlert({ message: t('subPlace.checkoutBeforeCheckin') });
                return;
            }
            hotelStartIso = localInputToUtc(`${subPlaceForm.checkInDate}T${subPlaceForm.checkInTime || '00:00'}`);
            hotelEndIso = localInputToUtc(`${subPlaceForm.checkOutDate}T${subPlaceForm.checkOutTime || '00:00'}`);

            // 일자 기준 겹침 검사 (시간 무시)
            const others = (subPlaces || []).filter(sp =>
                sp.category === 'HOTEL' && (!editingSubPlace || sp.subPlaceId !== editingSubPlace.subPlaceId)
            );
            const dateOf = (iso?: string) => (iso ? iso.slice(0, 10) : '');
            const aIn = subPlaceForm.checkInDate;
            const aOut = subPlaceForm.checkOutDate;
            const conflict = others.find(o => {
                const bIn = dateOf((o as any).startDatetime);
                const bOut = dateOf((o as any).endDatetime);
                if (!bIn || !bOut) return false;
                return aIn < bOut && aOut > bIn;
            });
            if (conflict) {
                await showAlert({ message: t('subPlace.hotelOverlap', { name: conflict.placeName }) });
                return;
            }
        }

        // 모든 API payload 에 공통으로 포함될 좌표/외부 placeId — 누락되면 미니지도 표시 깨짐
        const coordPayload = {
            ...(coordNum(subPlaceForm.latitude) !== undefined
                ? { latitude: coordNum(subPlaceForm.latitude) } : {}),
            ...(coordNum(subPlaceForm.longitude) !== undefined
                ? { longitude: coordNum(subPlaceForm.longitude) } : {}),
            ...(subPlaceForm.placeId ? { placeId: subPlaceForm.placeId } : {}),
        };

        console.log('[SubPlaceModal] PROCEEDING TO API CALL', {
            editingSubPlace: !!editingSubPlace,
            useDayDateTimeInputs,
            dayStartIso, dayEndIso,
            isHotel,
        });

        try {
            if (editingSubPlace) {
                // Update — DAY 비-HOTEL 이면 시작/종료 datetime 도 같이 갱신
                // useDayDateTimeInputs 모드면 visitTime 을 start 의 HH:mm 으로 동기화 (기존 표시와 호환)
                const updateVisitTime = useDayDateTimeInputs && subPlaceForm.startDatetimeLocal
                    ? subPlaceForm.startDatetimeLocal.slice(11, 16)
                    : subPlaceForm.visitTime;
                const res = await MeetingRepository.onPutUpdateSubPlace(meetingId, editingSubPlace.subPlaceId, {
                    placeName: subPlaceForm.placeName,
                    placeAddress: subPlaceForm.placeAddress,
                    category: subPlaceForm.category,
                    dayNumber: subPlaceForm.dayNumber,
                    visitTime: updateVisitTime,
                    startDatetime: dayStartIso || hotelStartIso,
                    endDatetime: dayEndIso || hotelEndIso,
                    description: subPlaceForm.description,
                    ...coordPayload,
                });
                if (!await okOrAlert(res)) return;

                // 수정 후에도 모임 종료보다 늦으면 모임 자동 연장
                if (needScheduleExtension && meetingScheduleId && meetingScheduleStartDatetime && dayEndIso) {
                    try {
                        await ScheduleRepository.onPutUpdateSchedule(meetingScheduleId, {
                            startDatetime: meetingScheduleStartDatetime,
                            endDatetime: dayEndIso,
                        });
                    } catch (e) {
                        console.warn('[SubPlaceModal] 수정 시 모임 종료 자동 연장 실패', e);
                    }
                }

                onClose();
                await showAlert({ message: t('subPlace.placeUpdated') });
            } else {
                // Create — 여행 모임 + HOTEL 아닌 경우 복수 일자 지원
                const baseSort = (subPlaces || []).filter(sp => sp.category === subPlaceForm.category).length + 1;
                if (isHotel) {
                    const res = await MeetingRepository.onPostCreateSubPlace(meetingId, {
                        placeName: subPlaceForm.placeName,
                        placeAddress: subPlaceForm.placeAddress,
                        category: 'HOTEL',
                        sortOrder: baseSort,
                        startDatetime: hotelStartIso,
                        endDatetime: hotelEndIso,
                        description: subPlaceForm.description,
                        ...coordPayload,
                    });
                    if (!await okOrAlert(res)) return;
                } else if (useDayDateTimeInputs) {
                    // DAY 모임 비-HOTEL 신규 — 시작/종료 datetime + visitTime(start 의 HH:mm) 저장
                    const visitTimeFromStart = subPlaceForm.startDatetimeLocal
                        ? subPlaceForm.startDatetimeLocal.slice(11, 16) // 'HH:mm'
                        : subPlaceForm.visitTime;
                    const res = await MeetingRepository.onPostCreateSubPlace(meetingId, {
                        placeName: subPlaceForm.placeName,
                        placeAddress: subPlaceForm.placeAddress,
                        category: subPlaceForm.category,
                        sortOrder: baseSort,
                        visitTime: visitTimeFromStart,
                        startDatetime: dayStartIso,
                        endDatetime: dayEndIso,
                        description: subPlaceForm.description,
                        ...coordPayload,
                    });
                    if (!await okOrAlert(res)) return;
                } else {
                    const pickedDays = isTravelMeeting
                        ? (subPlaceForm.dayNumbers && subPlaceForm.dayNumbers.length > 0
                            ? subPlaceForm.dayNumbers
                            : [subPlaceForm.dayNumber])
                        : [undefined];

                    if (pickedDays.length > 1) {
                        const items = pickedDays.map((d, idx) => ({
                            placeName: subPlaceForm.placeName,
                            placeAddress: subPlaceForm.placeAddress,
                            category: subPlaceForm.category,
                            sortOrder: baseSort + idx,
                            dayNumber: d as number,
                            visitTime: subPlaceForm.visitTime,
                            description: subPlaceForm.description,
                            ...coordPayload,
                        }));
                        const res = await MeetingRepository.onPostCreateSubPlaces(meetingId, items);
                        if (!await okOrAlert(res)) return;
                    } else {
                        const res = await MeetingRepository.onPostCreateSubPlace(meetingId, {
                            placeName: subPlaceForm.placeName,
                            placeAddress: subPlaceForm.placeAddress,
                            category: subPlaceForm.category,
                            sortOrder: baseSort,
                            dayNumber: pickedDays[0],
                            visitTime: subPlaceForm.visitTime,
                            description: subPlaceForm.description,
                            ...coordPayload,
                        });
                        if (!await okOrAlert(res)) return;
                    }
                }

                // DAY 비-HOTEL: 종료가 모임 schedule.endDatetime 보다 늦으면 모임 자동 연장
                if (needScheduleExtension && meetingScheduleId && meetingScheduleStartDatetime && dayEndIso) {
                    try {
                        await ScheduleRepository.onPutUpdateSchedule(meetingScheduleId, {
                            startDatetime: meetingScheduleStartDatetime,
                            endDatetime: dayEndIso,
                        });
                    } catch (e) {
                        console.warn('[SubPlaceModal] 모임 종료 자동 연장 실패 — 장소만 추가됨', e);
                    }
                }

                onClose();
                await showAlert({ message: t('subPlace.placeAdded') });
            }
            onSaveSuccess();
        } catch (error) {
            console.error('서브 장소 저장 실패:', error);
            await showAlert({ message: t('subPlace.saveFail') });
        }
    }, [meetingId, subPlaceForm, editingSubPlace, subPlaces, isTravelMeeting, onClose, onSaveSuccess, showAlert, showConfirm,
        useDayDateTimeInputs, meetingScheduleId, meetingScheduleStartDatetime, meetingScheduleEndDatetime, mainPlaceName,
        isMainPlaceEdit, onSaveMainPlace, autoAdjustConflicts, todayIso]);

    // 이전 sub_place / 호텔 좌표 — PlaceExplore/PlaceMapView 의 anchor 결정용
    const prevSubPlaceCenter = useMemo(() => {
        const places = (subPlaces || []).filter(sp =>
            sp.category !== 'HOTEL'
            && coordNum(sp.latitude) !== undefined && coordNum(sp.longitude) !== undefined
        );
        if (places.length === 0) return null;
        const isEditingThis = (sp: SubPlace) => editingSubPlace && sp.subPlaceId === editingSubPlace.subPlaceId;
        const others = places.filter(p => !isEditingThis(p));
        if (others.length === 0) return null;
        const sorted = [...others].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        return { lat: Number(sorted[0].latitude), lng: Number(sorted[0].longitude) };
    }, [subPlaces, editingSubPlace]);

    const hotelCenter = useMemo(() => {
        const hotels = (subPlaces || []).filter(sp =>
            sp.category === 'HOTEL'
            && coordNum(sp.latitude) !== undefined && coordNum(sp.longitude) !== undefined
        );
        if (hotels.length === 0) return null;
        const sorted = [...hotels].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        return { lat: Number(sorted[0].latitude), lng: Number(sorted[0].longitude) };
    }, [subPlaces]);

    // Handle map search navigation
    const handleMapSearch = useCallback(async () => {
        // 여행 모임 + 호텔 카테고리 → 여행지 좌표를 anchor 로 사용 (현 위치 시청 default 회피)
        const isTravelHotel = isTravelMeeting && subPlaceForm.category === 'HOTEL';
        const travelAnchor = isTravelHotel && travelDestinationCenter
            ? travelDestinationCenter
            : null;

        // 국내/해외/웹 모두 PlaceSearch 페이지로 일원화.
        // - 국내: PlaceSearch 가 웹 PlaceMapView(카카오) 렌더
        // - 해외 + 네이티브: PlaceSearch 가 openNativePlaceExplore(추천목록+검색+리뷰) 네이티브 화면으로 위임
        // PlaceSearch 가 placeInfo.lat/lng 가 있으면 currentLocation 으로 사용 → nearbyPlaces 가 그 좌표 기준 호출.
        const initialPlaceInfo = subPlaceForm.placeName
            ? { name: subPlaceForm.placeName, address: subPlaceForm.placeAddress, lat: 0, lng: 0 }
            : (travelAnchor ? {
                name: travelDestinationName || '',
                address: '',
                lat: travelAnchor.lat,
                lng: travelAnchor.lng,
            } : undefined);
        navigate('/meeting/place-search', {
            state: {
                returnTo,
                fromSubPlaceModal: true,
                subPlaceForm,
                editingSubPlaceId: editingSubPlace?.subPlaceId,
                isCustomVisitTime,
                meetingId,
                placeInfo: initialPlaceInfo,
                // anchor 결정용 — 이전 장소 → 호텔 → 여행지 우선순위로 화면이 활용
                isTravelMeeting,
                prevSubPlaceCenter,
                hotelCenter,
                travelDestinationCenter,
                allSubPlaces: subPlaces,
            }
        });
    }, [navigate, returnTo, subPlaceForm, editingSubPlace, isCustomVisitTime, meetingId, isTravelMeeting, travelDestinationName, travelDestinationCenter, prevSubPlaceCenter, hotelCenter, subPlaces]);

    // 추천 장소에서 찾기 — sub_place 전용 라우트로 진입 (모임 생성 step3 화면과 분리)
    const handleRecommendSearch = useCallback(() => {
        navigate('/meeting/sub-place-recommend', {
            state: {
                subPlaceMode: true,
                returnTo,
                fromSubPlaceModal: true,
                subPlaceForm,
                editingSubPlaceId: editingSubPlace?.subPlaceId,
                isCustomVisitTime,
                meetingId,
                // 숙박 모드 — PlaceExplore 가 이 flag 를 보고 카테고리·nearby API 를 숙박업소로 한정
                hotelOnlyMode: lockCategoryToHotel || subPlaceForm.category === 'HOTEL',
                // anchor 결정용
                isTravelMeeting,
                prevSubPlaceCenter,
                hotelCenter,
                travelDestinationCenter,
                allSubPlaces: subPlaces,
            }
        });
    }, [navigate, returnTo, subPlaceForm, editingSubPlace, isCustomVisitTime, meetingId, lockCategoryToHotel, isTravelMeeting, prevSubPlaceCenter, hotelCenter, travelDestinationCenter, subPlaces]);

    // Handle time picker navigation
    const handleTimePicker = useCallback(() => {
        navigate('/meeting/time-picker', {
            state: {
                returnTo,
                fromSubPlaceModal: true,
                subPlaceForm,
                editingSubPlaceId: editingSubPlace?.subPlaceId,
                meetingId // meetingId를 전달하여 돌아올 때도 유지
            }
        });
    }, [navigate, returnTo, subPlaceForm, editingSubPlace, meetingId]);

    /* 누르기 시작한 곳도 배경일 때만 닫습니다 — 누르는 사이 화면이
       움직이면 내용 위에서 시작한 탭이 배경에서 끝나 창이 닫힙니다. */
    const sheetBackdrop = useBackdropClose<HTMLDivElement>(onClose);

    useBackDismiss(isOpen, onClose);
    if (!isOpen) return null;

    return (
        <div className={cls("sub-place-modal-overlay")} {...sheetBackdrop}>
            <div className={cls("sub-place-modal")} onClick={(e) => e.stopPropagation()}>
                {/* rev3 · 상단 drag handle — 하단 슬라이드업 시트임을 시각적으로 명시 */}
                <div className={cls("sub-place-modal-handle-wrap")} onClick={onClose}>
                    <div className={cls("sub-place-modal-handle")} />
                </div>
                <div className={cls("sub-place-modal-header")}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                        <h2 className={cls("sub-place-modal-title")}>
                            {isMainPlaceEdit
                                ? t('subPlace.editTitle')
                                : editingSubPlace
                                    ? (editingSubPlace.category === 'HOTEL'
                                        ? t('subPlace.editHotelTitle')
                                        : isTravelMeeting
                                            ? t('subPlace.editAttractionTitle')
                                            : t('subPlace.editTitle'))
                                    : lockCategoryToHotel
                                        ? t('subPlace.addHotelTitle')
                                        : isTravelMeeting
                                            ? t('subPlace.addAttractionTitle')
                                            : t('subPlace.addTitle')}
                        </h2>
                        {isTravelMeeting && travelDestinationName ? (
                            <span style={{ fontSize: 11, color: 'var(--t-ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {travelDestinationName}
                            </span>
                        ) : null}
                    </div>
                    <button className={cls("sub-place-modal-close")} onClick={onClose}><IcClose size={16} /></button>
                </div>

                <div className={cls("sub-place-modal-content")}>
                    {/* Search buttons — 숙박 모드도 hotelOnlyMode 로 추천 API 를 숙박업소로 필터하므로 항상 노출 */}
                    <div className={cls("modal-form-group")}>
                        <button
                            type="button"
                            className={cls("map-search-btn recommend")}
                            onClick={handleRecommendSearch}
                        >
                            <span className={cls("map-search-icon")}><IcSparkle size={16} /></span>
                            <span className={cls("map-search-text")}>{t('subPlace.findFromRecommend')}</span>
                            <span className={cls("map-search-arrow")}>›</span>
                        </button>
                        <button
                            type="button"
                            className={cls("map-search-btn")}
                            onClick={handleMapSearch}
                            style={{ marginTop: 8 }}
                        >
                            <span className={cls("map-search-icon")}><IcMap size={16} /></span>
                            <span className={cls("map-search-text")}>{t('subPlace.searchOnMap')}</span>
                            <span className={cls("map-search-arrow")}>›</span>
                        </button>
                    </div>

                    {/* rev3+ · 시트 안 인라인 추천 프리뷰 (카드) + 카드 클릭 시 지도 시트 오픈 */}
                    {(() => {
                        if (isMainPlaceEdit) return null;
                        const cat = (subPlaceForm.category || '').toUpperCase();
                        const isHotelMode = lockCategoryToHotel || cat === 'HOTEL';
                        // anchor 우선순위 — 숙박은 여행지 중심, 관광은 이전 장소 > 호텔 > 여행지 순
                        const anchor: { lat: number; lng: number } | null = isHotelMode
                            ? (travelDestinationCenter || null)
                            : (prevSubPlaceCenter
                                || hotelCenter
                                || travelDestinationCenter
                                || null);
                        // typeConfigs — mode 별 카테고리 필터
                        const typeConfigs = isHotelMode
                            ? [{ type: 'hotel', limit: 20 }, { type: 'lodging', limit: 10 }]
                            : isTravelMeeting
                                ? (cat === 'RESTAURANT' ? [{ type: 'restaurant', limit: 25 }]
                                    : cat === 'CAFE' ? [{ type: 'cafe', limit: 25 }]
                                    : [{ type: 'attraction', limit: 25 }])
                                : [{ type: 'restaurant', limit: 15 }, { type: 'cafe', limit: 10 }];
                        const labelDefault = isHotelMode ? t('subPlace.nearbyHotel')
                            : cat === 'RESTAURANT' ? t('subPlace.nearbyRestaurant')
                            : cat === 'CAFE' ? t('subPlace.nearbyCafe')
                            : isTravelMeeting ? t('subPlace.nearbyAttraction')
                            : t('subPlace.nearbyPopular');
                        const mapSheetTitle = isHotelMode
                            ? t('subPlace.mapSheetTitleHotel')
                            : isTravelMeeting
                                ? t('subPlace.mapSheetTitleTravel')
                                : t('subPlace.mapSheetTitleDefault');
                        // rev4 · 마커 스타일 mode — 숙박은 가격 라벨, 여행 관광은 아이콘+별점 chip, DAY 모임 일반은 원형 pin
                        const markerMode: 'simple' | 'attraction' | 'hotel' = isHotelMode
                            ? 'hotel'
                            : isTravelMeeting
                                ? 'attraction'
                                : 'simple';
                        // 카드/CTA 클릭 → 지도 시트 오픈 (공통 핸들러)
                        const openMapFromPick = async (pick: RecommendPickResult) => {
                            const mapCenter = { lat: pick.lat || anchor?.lat || 0, lng: pick.lng || anchor?.lng || 0 };
                            // rev5 · 해외 + 네이티브 → 웹 슬라이드업 대신 네이티브 구글맵 직행 (기획서 §07-3a 의도 복원).
                            //   네이티브 지도는 자체 추천목록·검색·리뷰를 갖고 있어 웹 슬라이드업과 중복이었음.
                            if (isNativeApp() && mapCenter.lat && mapCenter.lng && !isInKorea(mapCenter.lat, mapCenter.lng)) {
                                const nativeMode: 'hotel' | 'attraction' | 'general' =
                                    isHotelMode ? 'hotel' : isTravelMeeting ? 'attraction' : 'general';
                                const result = await openNativePlaceExplore({
                                    places: (pick.lat && pick.lng)
                                        ? [{ id: pick.placeId || pick.name, name: pick.name, address: pick.address || '', lat: pick.lat, lng: pick.lng }]
                                        : [],
                                    center: mapCenter,
                                    confirmButtonText: t('subPlace.mapSheetConfirm'),
                                    title: t('subPlace.title'),
                                    apiUrl: config.API_URL,
                                    mode: nativeMode,
                                });
                                if (result) {
                                    setSubPlaceForm({
                                        ...subPlaceForm,
                                        placeName: result.name,
                                        placeAddress: result.address,
                                        latitude: result.lat || subPlaceForm.latitude,
                                        longitude: result.lng || subPlaceForm.longitude,
                                        placeId: (result as any).placeId || subPlaceForm.placeId,
                                    });
                                }
                                return;
                            }
                            // 국내 / 웹 브라우저 → 기존 웹 슬라이드업 지도 시트
                            setMapSheetState({
                                open: true,
                                center: mapCenter,
                                selectedId: pick.placeId || pick.name,
                                typeConfigs,
                                title: mapSheetTitle,
                                markerMode,
                                initialPick: pick,
                            });
                        };
                        // rev5 · 모드별 추천 3섹션 (general=일반모임 · korea=여행 관광 · overseas=숙박)
                        //   숙박 → overseas(Airbnb 스타일 stay 세트) · 여행 관광 → korea(사진 강조 명소 세트) · 그 외 → general
                        const recommendMode: RecommendMode = isHotelMode ? 'overseas' : isTravelMeeting ? 'korea' : 'general';
                        return (
                            <>
                                <div className={cls("modal-form-group")}>
                                    <RecommendSections
                                        mode={recommendMode}
                                        anchor={anchor}
                                        nearbyTypeConfigs={typeConfigs}
                                        meetingId={meetingId}
                                        nearbyLabel={labelDefault}
                                        onPick={openMapFromPick}
                                        onMore={handleRecommendSearch}
                                    />
                                </div>
                            </>
                        );
                    })()}

                    {/* Place name */}
                    <div className={cls("modal-form-group")}>
                        <label className={cls("modal-form-label")}>{t('subPlace.nameLabel')}</label>
                        <input
                            type="text"
                            className={cls("modal-form-input")}
                            placeholder={t('subPlace.namePlaceholder')}
                            value={subPlaceForm.placeName}
                            onChange={(e) => setSubPlaceForm({ ...subPlaceForm, placeName: e.target.value })}
                        />
                    </div>

                    {/* Address */}
                    <div className={cls("modal-form-group")}>
                        <label className={cls("modal-form-label")}>{t('subPlace.addressLabel')}</label>
                        <input
                            type="text"
                            className={cls("modal-form-input")}
                            placeholder={t('subPlace.addressPlaceholder')}
                            value={subPlaceForm.placeAddress}
                            onChange={(e) => setSubPlaceForm({ ...subPlaceForm, placeAddress: e.target.value })}
                        />
                    </div>

                    {/* Category - only for travel meetings, 본 장소 편집 모드는 숨김.
                        숙박 전용 모드(lockCategoryToHotel)에서는 chip 그룹 자체를 감춤 — HOTEL 로 잠금. */}
                    {isTravelMeeting && !isMainPlaceEdit && !lockCategoryToHotel && (
                        <div className={cls("modal-form-group")}>
                            <label className={cls("modal-form-label")}>{t('subPlace.categoryLabel')}</label>
                            <div className={cls("category-chips")}>
                                {(['ATTRACTION', 'RESTAURANT', 'CAFE', 'HOTEL'] as const)
                                    .filter(cat => !(hideHotelInCategoryPicker && cat === 'HOTEL'))
                                    .map(cat => (
                                    <button
                                        key={cat}
                                        type="button"
                                        className={cls(`category-chip ${subPlaceForm.category === cat ? 'selected' : ''}`)}
                                        onClick={() => setSubPlaceForm({ ...subPlaceForm, category: cat })}
                                    >
                                        {getCategoryIconEl(cat, 14)} {getCategoryLabel(cat)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* HOTEL 전용: 체크인 / 체크아웃 (시간 선택 옵션) */}
                    {isTravelMeeting && subPlaceForm.category === 'HOTEL' && (
                        <div className={cls("modal-form-group")}>
                            <label className={cls("modal-form-label")}>{t('subPlace.checkInOut')}</label>
                            <div className={cls("hotel-datetime-row")}>
                                <input
                                    type="date"
                                    className={cls("modal-form-input")}
                                    min={todayIso}
                                    value={subPlaceForm.checkInDate || ''}
                                    onChange={(e) => setSubPlaceForm({ ...subPlaceForm, checkInDate: e.target.value })}
                                />
                                <input
                                    type="time"
                                    className={cls("modal-form-input")}
                                    placeholder="--:--"
                                    value={subPlaceForm.checkInTime || ''}
                                    onChange={(e) => setSubPlaceForm({ ...subPlaceForm, checkInTime: e.target.value })}
                                />
                            </div>
                            <div className={cls("hotel-datetime-row")} style={{ marginTop: 8 }}>
                                <input
                                    type="date"
                                    className={cls("modal-form-input")}
                                    min={subPlaceForm.checkInDate || todayIso}
                                    value={subPlaceForm.checkOutDate || ''}
                                    onChange={(e) => setSubPlaceForm({ ...subPlaceForm, checkOutDate: e.target.value })}
                                />
                                <input
                                    type="time"
                                    className={cls("modal-form-input")}
                                    placeholder="--:--"
                                    value={subPlaceForm.checkOutTime || ''}
                                    onChange={(e) => setSubPlaceForm({ ...subPlaceForm, checkOutTime: e.target.value })}
                                />
                            </div>
                            {/* rev3 · 총 N박 M일 자동 계산 배지 (Day 몇~몇 자동 배정 안내) */}
                            {(() => {
                                const ci = subPlaceForm.checkInDate;
                                const co = subPlaceForm.checkOutDate;
                                if (!ci || !co) {
                                    return (
                                        <div className={cls("hotel-datetime-hint")}>{t('subPlace.checkInOutHint')}</div>
                                    );
                                }
                                const ciDate = new Date(ci);
                                const coDate = new Date(co);
                                if (isNaN(ciDate.getTime()) || isNaN(coDate.getTime())) return null;
                                const dayMs = 24 * 60 * 60 * 1000;
                                const nights = Math.round((coDate.getTime() - ciDate.getTime()) / dayMs);
                                if (nights < 0) {
                                    return (
                                        <div className={cls("hotel-stay-length invalid")}>
                                            {t('subPlace.checkOutBeforeIn')}
                                        </div>
                                    );
                                }
                                const days = nights + 1;
                                let dayRangeLabel = '';
                                if (travelPeriod) {
                                    const startDayIdx = Math.round((ciDate.getTime() - travelPeriod.start.getTime()) / dayMs) + 1;
                                    const endDayIdx = startDayIdx + Math.max(0, nights - 1);
                                    if (startDayIdx >= 1 && endDayIdx <= travelPeriod.days) {
                                        dayRangeLabel = nights <= 1
                                            ? t('subPlace.dayAssignedSingle', { n: startDayIdx })
                                            : t('subPlace.dayAssignedRange', { a: startDayIdx, b: endDayIdx });
                                    }
                                }
                                return (
                                    <div className={cls("hotel-stay-length")}>
                                        <span className={cls("stay-num")}>
                                            {nights === 0
                                                ? t('subPlace.stayDayUse')
                                                : t('subPlace.stayNightsDays', { nights, days })}
                                        </span>
                                        {dayRangeLabel ? <span className={cls("stay-days")}>· {dayRangeLabel}</span> : null}
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Day selection - only for travel meetings (HOTEL 제외) */}
                    {isTravelMeeting && travelPeriod && travelPeriod.days > 1 && subPlaceForm.category !== 'HOTEL' && (
                        <div className={cls("modal-form-group")}>
                            <label className={cls("modal-form-label")}>{t('subPlace.visitDay')}{!editingSubPlace && t('subPlace.multiSelectHint')}</label>
                            <div className={cls("day-chips")}>
                                {Array.from({ length: travelPeriod.days }, (_, i) => i + 1).map(day => {
                                    const selectedDays = editingSubPlace
                                        ? [subPlaceForm.dayNumber]
                                        : (subPlaceForm.dayNumbers || [subPlaceForm.dayNumber]);
                                    const isSelected = selectedDays.includes(day);
                                    // 해당 일차의 실제 날짜가 오늘보다 이전이면(이미 지난 날) 비활성화
                                    const dayDate = new Date(travelPeriod.start);
                                    dayDate.setDate(dayDate.getDate() + (day - 1));
                                    dayDate.setHours(0, 0, 0, 0);
                                    const todayMidnight = new Date();
                                    todayMidnight.setHours(0, 0, 0, 0);
                                    const isPast = dayDate.getTime() < todayMidnight.getTime();
                                    return (
                                        <button
                                            key={day}
                                            type="button"
                                            disabled={isPast}
                                            className={cls(`day-chip ${isSelected ? 'selected' : ''} ${isPast ? 'disabled' : ''}`)}
                                            onClick={() => {
                                                if (isPast) return;
                                                if (editingSubPlace) {
                                                    setSubPlaceForm({ ...subPlaceForm, dayNumber: day, dayNumbers: [day] });
                                                } else {
                                                    const current = subPlaceForm.dayNumbers || [subPlaceForm.dayNumber];
                                                    const next = current.includes(day)
                                                        ? current.filter(d => d !== day)
                                                        : [...current, day].sort((a, b) => a - b);
                                                    const finalDays = next.length === 0 ? [day] : next;
                                                    setSubPlaceForm({
                                                        ...subPlaceForm,
                                                        dayNumber: finalDays[0],
                                                        dayNumbers: finalDays,
                                                    });
                                                }
                                            }}
                                        >
                                            DAY {day}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* DAY 모임 비-HOTEL — 시작/종료 datetime 입력 (모임 시간 정보가 있을 때) */}
                    {useDayDateTimeInputs ? (
                        <>
                            <div className={cls("modal-form-group")}>
                                <label className={cls("modal-form-label")}>{t('subPlace.startTime')}</label>
                                <input
                                    type="datetime-local"
                                    className={cls("modal-form-input")}
                                    value={subPlaceForm.startDatetimeLocal || ''}
                                    onChange={(e) => setSubPlaceForm({ ...subPlaceForm, startDatetimeLocal: e.target.value })}
                                />
                            </div>
                            <div className={cls("modal-form-group")}>
                                <label className={cls("modal-form-label")}>
                                    {t('subPlace.endTime')}
                                    <span style={{ color: '#888', fontWeight: 400, fontSize: 11, marginLeft: 6 }}>
                                        {t('subPlace.endTimeHint')}
                                    </span>
                                </label>
                                <input
                                    type="datetime-local"
                                    className={cls("modal-form-input")}
                                    value={subPlaceForm.endDatetimeLocal || ''}
                                    onChange={(e) => setSubPlaceForm({ ...subPlaceForm, endDatetimeLocal: e.target.value })}
                                />
                            </div>
                        </>
                    ) : subPlaceForm.category !== 'HOTEL' && (
                        /* 여행 모임 비-HOTEL 또는 일반 모임 시간 정보 미전달 시 — 기존 방문 시간 chip UI */
                        <div className={cls("modal-form-group")}>
                            <label className={cls("modal-form-label")}>{t('subPlace.visitTime')}</label>
                            <div className={cls("visit-time-chips")}>
                                {visitTimeOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={cls(`visit-time-chip ${!isCustomVisitTime && subPlaceForm.visitTime === option.value ? 'selected' : ''}`)}
                                        onClick={() => {
                                            setIsCustomVisitTime(false);
                                            setSubPlaceForm({ ...subPlaceForm, visitTime: option.value });
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className={cls("visit-time-chip direct")}
                                    onClick={handleTimePicker}
                                >
                                    {t('subPlace.directInput')}
                                </button>
                            </div>

                            {/* Show custom time if selected */}
                            {isCustomVisitTime && subPlaceForm.visitTime && (
                                <div className={cls("selected-custom-visit-time")}>
                                    <div className={cls("custom-time-label")}>{t('subPlace.selectedTime')}</div>
                                    <div className={cls("custom-time-value")}>{subPlaceForm.visitTime}</div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Description */}
                    <div className={cls("modal-form-group")}>
                        <label className={cls("modal-form-label")}>{t('subPlace.memo')}</label>
                        <textarea
                            className={cls("modal-form-textarea")}
                            placeholder={t('subPlace.memoPlaceholder')}
                            value={subPlaceForm.description}
                            onChange={(e) => setSubPlaceForm({ ...subPlaceForm, description: e.target.value })}
                            rows={3}
                        />
                    </div>
                </div>

                <div className={cls("sub-place-modal-footer")}>
                    <button className={cls("modal-cancel-btn")} onClick={onClose}>
                        {t('subPlace.cancel')}
                    </button>
                    <button className={cls("modal-save-btn")} onClick={handleSave}>
                        {editingSubPlace ? t('subPlace.edit') : t('subPlace.add')}
                    </button>
                </div>
            </div>

            {/* rev3+ · 추천 지도 시트 — 카드 클릭 시 열리고 confirm 시 form 채움 */}
            <RecommendMapSheet
                isOpen={!!mapSheetState?.open}
                onClose={() => setMapSheetState(null)}
                initialCenter={mapSheetState?.center || null}
                initialSelectedId={mapSheetState?.selectedId || null}
                typeConfigs={mapSheetState?.typeConfigs || []}
                title={mapSheetState?.title || t('subPlace.mapSheetTitleDefault')}
                confirmLabel={t('subPlace.mapSheetConfirm')}
                markerMode={mapSheetState?.markerMode || 'simple'}
                onConfirm={(pick) => {
                    setSubPlaceForm({
                        ...subPlaceForm,
                        placeName: pick.name,
                        placeAddress: pick.address,
                        latitude: pick.lat || subPlaceForm.latitude,
                        longitude: pick.lng || subPlaceForm.longitude,
                        placeId: pick.placeId || subPlaceForm.placeId,
                    });
                    setMapSheetState(null);
                }}
            />
        </div>
    );
};

export { getCategoryLabel, getCategoryIconEl };
